import React, { useState, useContext } from 'react';
import {
  CircularProgress,
  Grid,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Typography,
  Paper,
} from '@material-ui/core';
import PropTypes from 'prop-types';
import { FileUploader } from 'react-drag-drop-files';

// Components
import { CommonDialog } from 'components';
import DragAndDropFiles from './DragAndDrop';
import FilePreview from './FilePreview';

// Context
import { Context } from 'context';
import { ADD_NOTIFICATION } from 'context/actions';

// Services
import { USER_SERVICES } from 'Services';

// Utils
import { getFileExtension, downloadFile } from 'utils';

// Styles
import useStyles from './styles';

const maxFileSize = 5;
const fileTypes = ['CSV'];

const UCR_FEE_SCHEDULE_TEMPLATE_FILE = 'UCR Fee Schedule.csv';
const UCR_FEE_SCHEDULE_TEMPLATE_PATH = `${process.env.PUBLIC_URL || ''}/templates/${encodeURIComponent(UCR_FEE_SCHEDULE_TEMPLATE_FILE)}`;

const UploadUcrFeesSchedule = (props) => {
  const {
    isOpen,
    onClose,
    levelData: { partner_id, group_id, region_id, location_id } = {},
    onUpdate,
  } = props;

  const { dispatch: globalDispatch } = useContext(Context);

  const [feesSchedule, setFeesSchedule] = useState({
    category: 'ucr_fees_schedule',
    fileName: '',
    file: null,
    sizeError: false,
    typeError: false,
  });
  const [uploading, setUploading] = useState(false);
  const [templateDownloading, setTemplateDownloading] = useState(false);

  const classes = useStyles();

  const { category, fileName, file, sizeError, typeError } = feesSchedule;
  const isValid =
    Boolean(fileName) &&
    Boolean(category) &&
    Boolean(file) &&
    !sizeError &&
    !typeError;

  const handleFileChange = (document) => {
    const documentArr = document?.name.split('.');
    const documentType = documentArr[1].toUpperCase();
    const documentName = documentArr[0];
    const fileSize = (document?.size / 1024 / 1024).toFixed(3);

    if (fileTypes.includes(documentType) && fileSize <= maxFileSize)
      setFeesSchedule({
        ...feesSchedule,
        file: document,
        fileName: fileName || documentName,
        typeError: false,
        sizeError: false,
      });
  };

  const onReplaceFile = () => {
    setFeesSchedule({
      ...feesSchedule,
      file: null,
      typeError: false,
      sizeError: false,
    });

    setTimeout(() => {
      const inputElement = document.querySelectorAll(
        '[name="import-fees-schedule"]',
      )?.[0];
  
      inputElement?.click();
    }, 100)

  };

  const dispatchGlobalNotification = (severity, message) =>
    globalDispatch({
      type: ADD_NOTIFICATION,
      payload: { notification: { severity, message } },
    });

  const clearUpload = () => {
    setFeesSchedule({
      category: 'ucr_fees_schedule',
      fileName: '',
      file: null,
      sizeError: false,
      typeError: false,
    });
  };

  const handleFeeScheduleTemplateDownload = async () => {
    if (templateDownloading) return;

    setTemplateDownloading(true);

    try {
      const res = await fetch(UCR_FEE_SCHEDULE_TEMPLATE_PATH);
      if (!res.ok) {
        throw new Error('Template file could not be loaded.');
      }
      const buf = await res.arrayBuffer();
      downloadFile(buf, 'text/csv', UCR_FEE_SCHEDULE_TEMPLATE_FILE);
      dispatchGlobalNotification('success', 'Template downloaded successfully.');
    } catch (err) {
      dispatchGlobalNotification(
        'error',
        err?.message || 'Error while downloading the sample template.',
      );
    } finally {
      setTemplateDownloading(false);
    }
  };

  const handleUpload = async () => {
    if (!file) return;

    setUploading(true);

    try {
      const fdata = new FormData();
      
      // Base payload with common fields
      const basePayload = {
        group_id,
        partner_id,
      };
      
      if (location_id) {
        fdata.append(
          'location',
          JSON.stringify({
            ...basePayload,
            location_id,
            region_id,
          })
        );
      } else {
        fdata.append('group', JSON.stringify(basePayload));
      }
      
      const renamedFile = new File(
        [file],
        `${fileName}${getFileExtension(file?.name)}`,
        {
          type: file.type,
          lastModified: file.lastModified,
        },
      );
      fdata.append(category, renamedFile);

      const response = await USER_SERVICES.uploadUcrFeesSchedule(group_id, fdata);

      if (response?.type === 'success') {
        dispatchGlobalNotification('success', 'File uploaded successfully');
        clearUpload();
        onClose();
        onUpdate?.('Settings');
      } else {
        const errMsg =
          response?.message ||
          response?.error ||
          response?.data?.message ||
          'Failed to upload the file';
        dispatchGlobalNotification('error', errMsg);
      }
    } catch (error) {
      const errMsg =
        typeof error === 'string'
          ? error
          : error?.message ||
            error?.response?.data?.message ||
            'Failed to upload the file';
      dispatchGlobalNotification('error', errMsg);
    } finally {
      setUploading(false);
    }
  };

  return (
    <CommonDialog
      open={isOpen}
      dialogTitle="Add Fee Schedule"
      primaryBtnTxt="Upload"
      primaryButtonStyles={{ minWidth: 125 }}
      secondaryBtnTxt="Cancel"
      isLoading={uploading}
      disabledPrimaryButton={!isValid || uploading}
      handleClose={onClose}
      handlePrimaryButtonClick={handleUpload}
      handleSecondaryButtonClick={onClose}
      maxWidth="md"
    >
      <Grid container spacing={4}>
        <Grid item lg={6} md={6} xs={12}>
          <FormControl fullWidth variant="outlined">
            <InputLabel htmlFor="outlined-Group-native-simple">
              Select Category
            </InputLabel>
            <Select
              id="pms-select-vendor"
              label="Select Category"
              name="category"
              value={category}
            >
              <MenuItem value="">Select Category</MenuItem>
              <MenuItem value="ucr_fees_schedule">UCR Fee Schedule</MenuItem>
            </Select>
          </FormControl>
        </Grid>
        <Grid item lg={6} md={6} xs={12}>
          <TextField
            fullWidth
            required={true}
            name="File Name"
            label="File Name"
            variant="outlined"
            value={fileName}
            onChange={(event) => {
              setFeesSchedule({
                ...feesSchedule,
                fileName: event?.target?.value.replace(/\./g, ''),
              });
            }}
          />
        </Grid>
        <Grid item xs={12}>
          <Paper className={classes.downloadLinkWrapper} elevation={0}>
            <Typography component="span" className="mr-10">
              Need Help?{' '}
              <Typography
                data-testid="download-ucr-fee-schedule-template-link"
                onClick={handleFeeScheduleTemplateDownload}
                color="secondary"
                component="span"
                style={{ cursor: 'pointer' }}
              >
                Download our file template
              </Typography>{' '}
              to make sure your file is formatted correctly.
            </Typography>
            {templateDownloading && (
              <Typography component="span" style={{ display: 'inline-flex' }}>
                <CircularProgress size={16} className="theme_color_orange" />
              </Typography>
            )}
          </Paper>
        </Grid>
        {!file && (
          <Grid item xs={12}>
            <FileUploader
              fileOrFiles={file}
              handleChange={handleFileChange}
              name="import-fees-schedule"
              types={fileTypes}
              maxSize={maxFileSize}
              minSize={0}
              onSizeError={() =>
                setFeesSchedule({ ...feesSchedule, sizeError: true })
              }
              onTypeError={() =>
                setFeesSchedule({ ...feesSchedule, typeError: true })
              }
            >
              <DragAndDropFiles
                fileTypes={fileTypes}
                maxFileSize={maxFileSize}
              />
              <Grid item xs={12}>
                {sizeError && (
                  <Typography className="color_error">
                    Upload Failed. Please upload a file less than {maxFileSize}
                    MB.
                  </Typography>
                )}

                {typeError && (
                  <Typography className="color_error">
                    Upload Failed. Please upload a CSV file.
                  </Typography>
                )}
              </Grid>
            </FileUploader>
          </Grid>
        )}
      </Grid>
      {file && (
        <FilePreview
          fileName={file?.name}
          size={file?.size}
          onReplaceFile={onReplaceFile}
        />
      )}
    </CommonDialog>
  );
};

UploadUcrFeesSchedule.propTypes = {
  isOpen: PropTypes.bool,
  onClose: PropTypes.func,
  levelData: PropTypes.object,
  onUpdate: PropTypes.func,
};

export default UploadUcrFeesSchedule;
